import React, { useState, useRef } from 'react';
import axios from 'axios';
import { Plus, X, Trash, Camera, Check } from '@phosphor-icons/react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const MAX_IMAGES = 10;

const MARRIAGE_TYPE_OPTIONS = [
  { key: 'hindu', label: 'Hindu' },
  { key: 'christian', label: 'Christian' },
  { key: 'muslim', label: 'Muslim' },
  { key: 'sikh', label: 'Sikh' },
  { key: 'brahmin', label: 'Brahmin' },
  { key: 'south_indian', label: 'South Indian' },
  { key: 'all', label: 'All Wedding Types' },
];

const photosOf = (pkg) => {
  if (Array.isArray(pkg?.photos) && pkg.photos.length) return pkg.photos;
  if (pkg?.photo) return [pkg.photo];
  return [];
};

const resolveSrc = (url) => (url?.startsWith('http') ? url : `${BACKEND_URL}${url}`);

/**
 * Vendor-side editor for "Custom Decoration Packages" — bundled offerings that a
 * decorator vendor wants to surface in the customer Instant Pricing → Wedding
 * flow under the new "Custom Packages" segment. Supports up to 10 photos per package.
 */
const CustomDecorPackages = ({ value, onChange }) => {
  const packages = value?.packages || [];
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState({ name: '', price: '', photos: [], description: '', marriage_types: ['all'] });
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  const setField = (k, v) => setDraft(prev => ({ ...prev, [k]: v }));

  const toggleMarriageType = (key) => {
    setDraft(prev => {
      const current = prev.marriage_types || [];
      const next = current.includes(key) ? current.filter(k => k !== key) : [...current, key];
      let cleaned = next;
      if (key === 'all') cleaned = current.includes('all') ? [] : ['all'];
      else cleaned = next.filter(k => k !== 'all');
      return { ...prev, marriage_types: cleaned };
    });
  };

  const handlePhotoUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    const remaining = MAX_IMAGES - (draft.photos?.length || 0);
    if (remaining <= 0) {
      toast.error(`Max ${MAX_IMAGES} images per package`);
      if (fileInputRef.current) fileInputRef.current.value = '';
      return;
    }
    const toUpload = files.slice(0, remaining);
    if (files.length > remaining) {
      toast.warning(`Only ${remaining} more image${remaining !== 1 ? 's' : ''} allowed — uploading the first ${remaining}`);
    }
    setUploading(true);
    try {
      const urls = [];
      for (const file of toUpload) {
        const fd = new FormData();
        fd.append('file', file);
        const { data } = await axios.post(`${BACKEND_URL}/api/upload`, fd, {
          headers: { 'Content-Type': 'multipart/form-data' },
          withCredentials: true,
        });
        urls.push(data.url);
      }
      setDraft(prev => ({ ...prev, photos: [...(prev.photos || []), ...urls] }));
    } catch (err) {
      toast.error('Upload failed');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const removePhoto = (idx) => {
    setDraft(prev => ({ ...prev, photos: (prev.photos || []).filter((_, i) => i !== idx) }));
  };

  const startNew = () => {
    setDraft({ name: '', price: '', photos: [], description: '', marriage_types: ['all'] });
    setEditing(true);
  };

  const cancelEdit = () => {
    setEditing(false);
    setDraft({ name: '', price: '', photos: [], description: '', marriage_types: ['all'] });
  };

  const savePackage = () => {
    const name = draft.name.trim();
    const price = parseInt(draft.price) || 0;
    const photos = draft.photos || [];
    if (!name) { toast.error('Package name required'); return; }
    if (!price) { toast.error('Price required'); return; }
    if (!photos.length) { toast.error('At least one image required'); return; }
    const mts = draft.marriage_types?.length ? draft.marriage_types : ['all'];
    const newPkg = {
      id: `cp_${Date.now()}`,
      name, price,
      photos,
      photo: photos[0], // legacy compat — keep first photo on the old key for older clients
      description: draft.description.trim(),
      marriage_types: mts,
    };
    onChange({ packages: [...packages, newPkg] });
    cancelEdit();
    toast.success(`Package "${name}" added with ${photos.length} image${photos.length !== 1 ? 's' : ''}`);
  };

  const removePackage = (id) => {
    onChange({ packages: packages.filter(p => p.id !== id) });
  };

  const photosCount = (draft.photos || []).length;

  return (
    <div className="space-y-3">
      <input type="file" accept="image/*" multiple ref={fileInputRef} onChange={handlePhotoUpload} className="hidden" />
      <p className="text-sm text-text-secondary">Bundle multiple decorations into a fixed-price wedding package. Upload up to {MAX_IMAGES} photos per package — they appear in a swipeable gallery on the customer side.</p>

      {/* Existing packages */}
      {packages.length > 0 && (
        <div className="space-y-2" data-testid="custom-decor-packages-list">
          {packages.map((p) => {
            const photos = photosOf(p);
            return (
              <div key={p.id} className="border-2 border-purple-300 bg-purple-50/30 rounded-md p-3 flex items-start gap-3">
                {photos[0] && (
                  <div className="relative flex-shrink-0">
                    <img src={resolveSrc(photos[0])} alt={p.name}
                      className="w-20 h-20 object-cover rounded" />
                    {photos.length > 1 && (
                      <span className="absolute -bottom-1 -right-1 bg-purple-600 text-white text-[10px] font-bold px-1.5 py-0.5 rounded shadow-md">
                        +{photos.length - 1}
                      </span>
                    )}
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <div className="font-heading text-lg font-medium truncate">{p.name}</div>
                  <div className="font-heading text-xl text-brand-primary mt-0.5">₹{Number(p.price).toLocaleString('en-IN')}</div>
                  <div className="text-xs text-text-secondary mt-1 flex flex-wrap gap-1">
                    {(p.marriage_types || []).map(mt => (
                      <span key={mt} className="px-1.5 py-0.5 bg-white border border-purple-200 rounded text-[10px] uppercase tracking-wider">
                        {MARRIAGE_TYPE_OPTIONS.find(o => o.key === mt)?.label || mt}
                      </span>
                    ))}
                  </div>
                  {p.description && <p className="text-xs text-text-secondary mt-1 line-clamp-2">{p.description}</p>}
                </div>
                <Button type="button" onClick={() => removePackage(p.id)}
                  variant="ghost" size="sm" className="text-red-500 hover:text-red-700"
                  data-testid={`remove-custom-pkg-${p.id}`}>
                  <Trash size={14} />
                </Button>
              </div>
            );
          })}
        </div>
      )}

      {/* Add form */}
      {!editing ? (
        <Button type="button" onClick={startNew}
          className="w-full bg-purple-600 hover:bg-purple-700 text-white rounded-none"
          data-testid="add-custom-pkg-btn">
          <Plus size={16} className="mr-1" /> Add Simple Wedding Decoration
        </Button>
      ) : (
        <div className="border-2 border-purple-400 bg-purple-50/20 rounded-md p-3 space-y-2">
          <div className="text-xs tracking-[0.2em] uppercase font-bold text-purple-700">New Simple Wedding Decoration</div>

          {/* Photo gallery */}
          <div>
            <Label className="text-xs tracking-[0.2em] uppercase font-bold text-text-secondary mb-1 block">
              Photos {photosCount > 0 && <span className="text-purple-600">({photosCount}/{MAX_IMAGES})</span>}
              {photosCount === 0 && <span className="text-red-500"> *</span>}
            </Label>
            <div className="flex flex-wrap gap-2">
              {(draft.photos || []).map((url, idx) => (
                <div key={idx} className="relative w-20 h-20 rounded overflow-hidden border-2 border-purple-300 group" data-testid={`custom-pkg-photo-${idx}`}>
                  <img src={resolveSrc(url)} alt="" className="w-full h-full object-cover" />
                  <button type="button" onClick={() => removePhoto(idx)}
                    className="absolute top-0 right-0 bg-red-500 hover:bg-red-600 text-white p-0.5 rounded-bl opacity-0 group-hover:opacity-100 transition"
                    data-testid={`custom-pkg-photo-remove-${idx}`}>
                    <X size={12} />
                  </button>
                  {idx === 0 && (
                    <span className="absolute bottom-0 left-0 bg-purple-600 text-white text-[9px] font-bold px-1 py-0.5 uppercase tracking-wider">Cover</span>
                  )}
                </div>
              ))}
              {photosCount < MAX_IMAGES && (
                <div
                  className={`w-20 h-20 border-2 ${photosCount === 0 ? 'border-dashed border-red-400 bg-red-50' : 'border-dashed border-purple-300 hover:bg-purple-50'} rounded flex items-center justify-center cursor-pointer transition`}
                  onClick={() => fileInputRef.current?.click()}
                  data-testid="custom-pkg-photo-add"
                >
                  {uploading ? (
                    <span className="text-xs text-text-secondary">...</span>
                  ) : (
                    <div className="flex flex-col items-center">
                      <Plus size={18} className={photosCount === 0 ? 'text-red-500' : 'text-purple-600'} />
                      <span className={`text-[10px] mt-0.5 ${photosCount === 0 ? 'text-red-500' : 'text-purple-600'}`}>
                        {photosCount === 0 ? 'Required' : 'Add'}
                      </span>
                    </div>
                  )}
                </div>
              )}
            </div>
            <p className="text-[11px] text-text-secondary mt-1">First image is the cover. You can select multiple files at once.</p>
          </div>

          {/* Name + Price */}
          <div className="space-y-2">
            <Input value={draft.name} onChange={(e) => setField('name', e.target.value)}
              placeholder="Package name (e.g. Royal Hindu Mandap Bundle) *"
              className="rounded-none border-[#E8E1D5] h-9 text-sm"
              data-testid="custom-pkg-name-input" />
            <div className="flex items-center gap-1">
              <span className="text-sm text-brand-primary">₹</span>
              <Input type="number" value={draft.price} onChange={(e) => setField('price', e.target.value)}
                placeholder="Total package price *"
                className="rounded-none border-[#E8E1D5] h-9 text-sm flex-1"
                data-testid="custom-pkg-price-input" />
            </div>
          </div>

          {/* Marriage types */}
          <div>
            <Label className="text-xs tracking-[0.2em] uppercase font-bold text-text-secondary mb-1 block">Applicable Wedding Types</Label>
            <div className="flex flex-wrap gap-1.5">
              {MARRIAGE_TYPE_OPTIONS.map(opt => {
                const active = draft.marriage_types?.includes(opt.key);
                return (
                  <button type="button" key={opt.key} onClick={() => toggleMarriageType(opt.key)}
                    className={`text-xs px-2 py-1 border-2 transition-all ${active ? 'border-purple-600 bg-purple-100 text-purple-800 font-bold' : 'border-[#E8E1D5] text-text-secondary'}`}
                    data-testid={`custom-pkg-mt-${opt.key}`}>
                    {active && <Check size={10} className="inline mr-0.5" />}
                    {opt.label}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Description */}
          <div>
            <Label className="text-xs tracking-[0.2em] uppercase font-bold text-text-secondary mb-1 block">Description (optional)</Label>
            <Input value={draft.description} onChange={(e) => setField('description', e.target.value)}
              placeholder="What's included? e.g. Mandap + entrance arch + 2 stages + lighting"
              className="rounded-none border-[#E8E1D5] h-9 text-sm"
              data-testid="custom-pkg-desc-input" />
          </div>

          {/* Actions */}
          <div className="flex gap-2 justify-end pt-1">
            <Button type="button" onClick={cancelEdit} variant="ghost" size="sm">
              <X size={14} className="mr-1" /> Cancel
            </Button>
            <Button type="button" onClick={savePackage}
              className="bg-purple-600 hover:bg-purple-700 text-white rounded-none"
              data-testid="custom-pkg-save-btn">
              <Check size={14} className="mr-1" /> Add Package
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default CustomDecorPackages;
